// import { LE } from "@/constants/enumsNew";
import { LE } from "@/constants/enums";
import type {
  ContainerData,
  DistributorReport,
  DistributorReportContainer,
  DistributorReportFormKit,
  DistributorReportNew,
} from "@/views/distributorReport/distributorReport";
import { murnAddressFromFormkit, murnAddressToFormkit } from "@modernary/mui/components/fk";
import { fillPrefixedAddressLookupValues } from "@modernary/mui/components/lookup";
import { calculateContainerFee, calculateDepositAmount, calculateTotalDue, calculateTotals } from "./helpers";
import { assignFilesAndDocsByDocType, FileDocPropertyName } from "@modernary/mui/components/document";

export default {
  addAuthRequestFields,
  getInitialData,
  skinnyResponseAdapter,
  requestAdapter,
  responseAdapter,
  toFormkit,
  fromFormkit,
};

export const drDocTypeToFieldMap = new Map<LE.DocumentType, FileDocPropertyName>([
  [LE.DocumentType.ExportedSupport, new FileDocPropertyName("containersExported")],
  [LE.DocumentType.SoldDonatedTransferedSupport, new FileDocPropertyName("containersSoldDonatedOrTransfered")],
]);

function addAuthRequestFields<T extends DistributorReport | DistributorReportNew>(entity: T) {
  entity._authRequestFields = ["id", "distributorId", "statusMurn", "isReportStarted"] as (keyof DistributorReport)[];
  return entity;
}

function getInitialData() {
  const result = {
    typeMurn: LE.ReportType.DistributorReport,
    statusMurn: LE.ReportStatus.Scheduled,
    documents: [],
  } as unknown as DistributorReport;
  //TODO:TS
  return addAuthRequestFields(result);
}

/** Be sure to await this when using it. Commonly used for lists and child lists */
async function skinnyResponseAdapter<T extends DistributorReport | DistributorReportNew>(response: { data: T }) {
  addAuthRequestFields(response.data);

  return response.data;
}

/** Be sure to await this when using it. */
async function responseAdapter<T extends DistributorReport | DistributorReportNew>(response: { data: T }) {
  if (response.data.distributorReportContainers) {
    let allContainerCounts = 0;
    //Convert the shape of the containers list to the shape of the form. We use the container murn to find the corresponding group name in the form.
    response.data.distributorReportContainers.forEach((container) => {
      // if (!container.containerMaterialTypeMurn) return;
      // const fkGroupName = containerMurnToFormkitGroupMapping.get(container.containerMaterialTypeMurn);
      // if (!fkGroupName) return;
      // response.data[fkGroupName] = container as any;
      if (container.containerCount) allContainerCounts += container.containerCount;
    });

    response.data._totals = calculateTotals(allContainerCounts);
  }
  await skinnyResponseAdapter(response);

  assignFilesAndDocsByDocType(drDocTypeToFieldMap, response.data);

  await fillPrefixedAddressLookupValues(response.data, ["mail"]);

  return response.data;
}

function requestAdapter() {}

const containerMurnToFormkitGroupMapping = new Map<LE.ContainerMaterialType, keyof DistributorReport>([
  [LE.ContainerMaterialType.Aluminum, "_aluminum"],
  [LE.ContainerMaterialType.BiMetal, "_biMetal"],
  [LE.ContainerMaterialType.PlasticPETE, "_plasticPETE"],
  [LE.ContainerMaterialType.PlasticHDPE, "_plasticHDPE"],
  [LE.ContainerMaterialType.Glass, "_glass"],
]);

// Convert server data for use in the page's form (which may have groupings, etc different from the server data)
function toFormkit<T extends DistributorReport | DistributorReportNew>(data: T) {
  murnAddressToFormkit(data, "mail");
  //TODO: Mask Bug phone number only set to be undefined to fix formkit bug. Once formkit resolves bug remove
  if (!data.contactPhone) {
    data.contactPhone = undefined;
  }

  if (data.distributorReportContainers) {
    let allContainerCounts = 0;
    //Convert the shape of the containers list to the shape of the form. We use the container murn to find the corresponding group name in the form.
    data.distributorReportContainers.forEach((container) => {
      if (!container.containerMaterialTypeMurn) return;
      const fkGroupName = containerMurnToFormkitGroupMapping.get(container.containerMaterialTypeMurn);
      if (!fkGroupName) return;
      data[fkGroupName] = container as any;
    });
  }
}

// Convert formkit data to prep it for sending to the server (which is probably flatter than the fk data)
function fromFormkit(data: DistributorReportFormKit | DistributorReport) {
  murnAddressFromFormkit(data, "mail");
  //TODO: Mask Bug remove this once the if above is removed.
  if (data.contactPhone === undefined) {
    data.contactPhone = null;
  }

  //Comvert the form grouping of the containers back into the shape the BE expects which is of DistributorReportContainer
  const distributorContainers: DistributorReportContainer[] = [];

  if (data._aluminum?.containerCount) {
    const container: DistributorReportContainer = {
      containerMaterialTypeMurn: LE.ContainerMaterialType.Aluminum,
      ...data._aluminum,
      distributorReportId: data.id,
    };
    distributorContainers.push(container);
  }
  if (data._biMetal?.containerCount) {
    const container: DistributorReportContainer = {
      containerMaterialTypeMurn: LE.ContainerMaterialType.BiMetal,
      ...data._biMetal,
      distributorReportId: data.id,
    };
    distributorContainers.push(container);
  }
  if (data._plasticPETE?.containerCount) {
    const container: DistributorReportContainer = {
      containerMaterialTypeMurn: LE.ContainerMaterialType.PlasticPETE,
      ...data._plasticPETE,
      distributorReportId: data.id,
    };
    distributorContainers.push(container);
  }
  if (data._plasticHDPE?.containerCount) {
    const container: DistributorReportContainer = {
      containerMaterialTypeMurn: LE.ContainerMaterialType.PlasticHDPE,
      ...data._plasticHDPE,
      distributorReportId: data.id,
    };
    distributorContainers.push(container);
  }

  if (data._glass?.containerCount) {
    const container: DistributorReportContainer = {
      containerMaterialTypeMurn: LE.ContainerMaterialType.Glass,
      ...data._glass,
      distributorReportId: data.id,
    };
    distributorContainers.push(container);
  }

  data.distributorReportContainers = distributorContainers;
}
